/**
 * Axios Configuration
 * Base Axios instance with default settings
 */

import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../constants/api.constants';
import { STORAGE_KEYS } from '../constants';

const FORCE_RESET_FLAG_KEY = 'ub_force_reset_required';

/**
 * Axios instance configuration
 */
const axiosConfig: AxiosRequestConfig = {
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false, // Set to true if using cookies
};

/**
 * Create Axios instance
 */
export const axiosInstance: AxiosInstance = axios.create(axiosConfig);

/**
 * Get token from localStorage
 */
const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Request Interceptor
 * Automatically adds Bearer token to all requests
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Normalize API base path:
    // - If baseURL already ends with /api/v1, avoid double-prefix when callers use /api/v1/...
    // - If baseURL is domain-only, auto-prefix /api/v1 for legacy callers using /agencies, /auth, etc.
    const baseURL = String(config.baseURL || '');
    const url = String(config.url || '');
    const baseHasV1 = /\/api\/v1\/?$/.test(baseURL);
    const urlHasV1 = url.startsWith('/api/v1/');
    if (baseHasV1 && urlHasV1) {
      config.url = url.replace(/^\/api\/v1/, '');
    } else if (!baseHasV1 && url.startsWith('/') && !urlHasV1) {
      config.url = `/api/v1${url}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles common HTTP error scenarios globally
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const response = error.response;
    if (!response) return Promise.reject(error);

    const status = response.status;
    const errorData = response.data as any;
    const errorCode = errorData?.code;

    switch (status) {
      case 401:
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        break;

      case 403:
        if (errorCode === 'PASSWORD_RESET_REQUIRED') {
          // Avoid double-popups:
          // - If we're already on /login, let the page handle showing the modal.
          // - If this happened on the login request itself, let the login page handle it.
          const requestUrl = String((error as any)?.config?.url || '');
          const isLoginRequest =
            requestUrl.includes('/api/v1/auth/login') || requestUrl.includes('/auth/login');

          if (window.location.pathname === '/login' || isLoginRequest) {
            break;
          }

          // Ensure the user lands on login and sees the forced reset modal.
          // We can't open UI from here, so we set a flag for the login page.
          try {
            sessionStorage.setItem(FORCE_RESET_FLAG_KEY, '1');
          } catch {
            // ignore storage errors
          }
          window.location.href = '/login';
          break;
        }
        if (errorCode === 'EMAIL_NOT_VERIFIED') {
          window.location.href = '/verify-email';
          break;
        }
        // Feature-level 403s (e.g. staffing caseload) should stay on the page
        // and be handled by the caller with a toast — do not hard-navigate.
        break;

      case 500:
      case 502:
      case 503:
        // Stay on the current page so feature screens (e.g. Staffing Board)
        // can show an inline error instead of forcing /500 navigation.
        break;
    }

    return Promise.reject(error);
  }
);

/**
 * Export default instance
 */
export default axiosInstance;

